// ============================================================================
// MÓDULO DE SERVIÇOS: LEGAL APIS (BRAZIL & DATAJUD)
// ARQUIVO: src/services/LegalAPIs.js
// OBJETIVO: Centralizar chamadas externas via Proxy Serverless.
// ATUALIZAÇÃO V69.0: Integração Real via Cloud Function (BFF Pattern).
// ============================================================================

import { getMockDataJud, parseDataJudResponse } from "../utils/DataJudParser";

// URL da Cloud Function (Ajuste conforme seu deploy ou emulador local)
// Se estiver rodando localmente (emulator): "http://127.0.0.1:5001/portalrda-f312d/southamerica-east1/consultarProcesso"
// Em produção: "https://southamerica-east1-portalrda-f312d.cloudfunctions.net/consultarProcesso"
const PROXY_URL =
  "https://southamerica-east1-portalrda-f312d.cloudfunctions.net/consultarProcesso";

const maskGeminiValue = (value) => {
  const safe = String(value || "").trim();
  if (!safe) return "<EMPTY>";
  if (safe.length <= 8) return `${safe.slice(0, 2)}***`;
  return `${safe.slice(0, 6)}...${safe.slice(-4)}`;
};

const readGeminiRuntimeConfig = () => {
  const proxyUrl = String(import.meta.env.VITE_GEMINI_PROXY_URL || "").trim();
  const apiKey = String(import.meta.env.VITE_GEMINI_API_KEY || "").trim();
  const fastModel =
    String(
      import.meta.env.VITE_GEMINI_MODEL_FAST ||
        import.meta.env.VITE_GEMINI_MODEL ||
        "gemini-1.5-flash",
    ).trim() || "gemini-1.5-flash";
  const proModel =
    String(
      import.meta.env.VITE_GEMINI_MODEL_PRO || "gemini-2.5-flash",
    ).trim() || "gemini-2.5-flash";

  console.info("[LegalAPIs] Gemini runtime config", {
    proxyConfigured: Boolean(proxyUrl),
    proxyPreview: maskGeminiValue(proxyUrl),
    apiKeyConfigured: Boolean(apiKey),
    apiKeyPreview: maskGeminiValue(apiKey),
    fastModel,
    proModel,
  });

  return { proxyUrl, apiKey, fastModel, proModel };
};

const normalizeGeminiTier = (tier) => {
  const safeTier = String(tier || "")
    .trim()
    .toLowerCase();
  if (safeTier === "fast") return "fast";
  return "pro";
};

const buildGeminiModelCandidates = (preferredModel, tier = "pro") => {
  const safeTier = normalizeGeminiTier(tier);
  const primary =
    String(preferredModel || "").trim() ||
    (safeTier === "fast" ? "gemini-1.5-flash" : "gemini-2.5-flash");
  return Array.from(
    new Set(
      [
        primary,
        ...(safeTier === "fast"
          ? [
              "gemini-1.5-flash-latest",
              "gemini-2.0-flash-lite",
              "gemini-2.0-flash",
            ]
          : [
              "gemini-2.5-flash",
              "gemini-2.5-flash-lite",
              "gemini-1.5-pro",
              "gemini-1.5-pro-latest",
              "gemini-2.0-flash",
            ]),
      ].filter(Boolean),
    ),
  );
};

const fetchGeminiAvailableModels = async (apiKey) => {
  try {
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models?key=${encodeURIComponent(apiKey)}`,
    );
    if (!response.ok) {
      const errText = await response.text();
      console.warn("[LegalAPIs] Falha ao listar modelos Gemini", {
        status: response.status,
        errText,
      });
      return [];
    }

    const payload = await response.json();
    const models = Array.isArray(payload?.models) ? payload.models : [];
    return models
      .filter(
        (item) =>
          Array.isArray(item?.supportedGenerationMethods) &&
          item.supportedGenerationMethods.includes("generateContent"),
      )
      .map((item) =>
        String(item?.name || "")
          .replace(/^models\//, "")
          .trim(),
      )
      .filter(Boolean);
  } catch (error) {
    console.warn("[LegalAPIs] Erro ao consultar listModels do Gemini", error);
    return [];
  }
};

const resolveGeminiModelCandidates = async (
  apiKey,
  preferredModel,
  tier = "pro",
) => {
  const safeTier = normalizeGeminiTier(tier);
  const staticCandidates = buildGeminiModelCandidates(preferredModel, safeTier);
  const availableModels = await fetchGeminiAvailableModels(apiKey);

  if (!availableModels.length) {
    return staticCandidates;
  }

  const preferredMatches = staticCandidates.filter((candidate) =>
    availableModels.includes(candidate),
  );
  const prioritizedAvailable = availableModels.filter((candidate) => {
    if (safeTier === "fast") return /flash|lite/i.test(candidate);
    return (
      /pro|2\.5|thinking|exp/i.test(candidate) ||
      (/flash/i.test(candidate) && !/lite/i.test(candidate))
    );
  });
  const fallbackAvailable = availableModels.filter(
    (candidate) => !prioritizedAvailable.includes(candidate),
  );

  const combined = Array.from(
    new Set([
      ...preferredMatches,
      ...prioritizedAvailable,
      ...fallbackAvailable,
      ...staticCandidates,
    ]),
  );

  console.info("[LegalAPIs] Modelos Gemini elegíveis", {
    tier: safeTier,
    preferredModel,
    preferredMatches,
    availableCount: availableModels.length,
    candidates: combined,
  });

  return combined;
};

const fetchGeminiGenerateContent = async ({
  apiKey,
  modelCandidates,
  body,
}) => {
  let lastError = null;

  for (const candidateModel of modelCandidates) {
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(candidateModel)}:generateContent?key=${encodeURIComponent(apiKey)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    );

    if (response.ok) {
      return response;
    }

    const errText = await response.text();
    lastError = new Error(
      `Erro Gemini Direct (${response.status}) [model=${candidateModel}]: ${errText}`,
    );

    if (response.status !== 404) {
      throw lastError;
    }

    console.warn("[LegalAPIs] Modelo Gemini indisponível, tentando fallback", {
      candidateModel,
    });
  }

  throw lastError || new Error("Falha ao acessar modelos Gemini Direct.");
};

const extractGeminiTextFromResponse = (payload) => {
  if (!payload) return "";

  if (typeof payload === "string") return String(payload || "").trim();

  const directText = String(
    payload?.text ||
      payload?.output ||
      payload?.result ||
      payload?.content ||
      "",
  ).trim();
  if (directText) return directText;

  const candidateParts = payload?.candidates?.[0]?.content?.parts;
  if (Array.isArray(candidateParts)) {
    const joined = candidateParts
      .map((part) => String(part?.text || "").trim())
      .filter(Boolean)
      .join("\n")
      .trim();
    if (joined) return joined;
  }

  return "";
};

export const generateTextWithGemini = async (
  systemPrompt,
  userText,
  options = {},
) => {
  const prompt = String(systemPrompt || "").trim();
  const input = String(userText || "").trim();
  const { apiKey, fastModel, proModel } = readGeminiRuntimeConfig();
  const tier = normalizeGeminiTier(options?.tier || "fast");
  const model = tier === "pro" ? proModel : fastModel;

  if (!prompt) throw new Error("Prompt textual Gemini ausente.");
  if (!input) throw new Error("Conteúdo textual ausente para sumarização.");

  if (!apiKey) {
    throw new Error(
      "Gemini textual não configurado (defina VITE_GEMINI_API_KEY).",
    );
  }

  try {
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model || "gemini-1.5-flash")}:generateContent?key=${encodeURIComponent(apiKey)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [
            {
              parts: [{ text: prompt }, { text: input }],
            },
          ],
          generationConfig: {
            temperature: 0.2,
            topP: 0.9,
            maxOutputTokens: 700,
          },
        }),
      },
    );

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(
        `Erro Gemini Direct (${response.status}) [model=${model}]: ${errText}`,
      );
    }

    const payload = await response.json();
    const out = extractGeminiTextFromResponse(payload);
    if (!out)
      throw new Error(
        "Gemini Direct retornou resposta vazia para sumarização textual.",
      );
    return out;
  } catch (error) {
    throw new Error(
      `Falha na sumarização textual Gemini: ${String(error?.message || error)}`,
    );
  }
};

// ❌ REMOVIDO em 01/09/2026 (item 5 do PRE_VOO_CADERNO_SINTESE_20260901.md):
// resumirMovimento() — chamava o Gemini direto do navegador pra resumir uma
// movimentação, disparado por um clique de UI (MovementResumoPanel.jsx,
// botão "Gerar resumo (IA)"). Nunca foi o caminho certo (Constituição de
// Processos Art. 13.7, 22/08/2026) e confirmado em uso real, com resumo já
// gerado e sentado em produção sem revisão (caso [PET_A]). Quem escreve um
// resumo de movimento agora é o Claude Code em sessão dirigida, direto no
// campo `resumo` via updateMovementResumo — nunca mais um botão chamando
// API externa. generateTextWithGemini/analyzeImageWithGemini abaixo
// continuam existindo — são usados por outras telas (WordDocumentService.js,
// ClientNotesTab.jsx, AIDocumentService.js), fora do escopo desta limpeza.

export const analyzeImageWithGemini = async (
  base64Data,
  mimeType,
  systemPrompt,
  options = {},
) => {
  const data = String(base64Data || "").trim();
  const safeMimeType = String(mimeType || "").trim() || "image/jpeg";
  const prompt = String(systemPrompt || "").trim();
  const { apiKey } = readGeminiRuntimeConfig();

  if (!data)
    throw new Error("Imagem em Base64 ausente para análise multimodal.");
  if (!prompt) throw new Error("Prompt multimodal ausente.");

  if (!apiKey) {
    throw new Error(
      "Gemini multimodal não configurado (defina VITE_GEMINI_API_KEY).",
    );
  }

  try {
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${encodeURIComponent(apiKey)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                { text: prompt },
                { inlineData: { mimeType: safeMimeType, data } },
              ],
            },
          ],
          generationConfig: {
            responseMimeType: "application/json",
          },
        }),
      },
    );

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Erro Gemini Direct (${response.status}): ${errText}`);
    }

    const payload = await response.json();
    const out = extractGeminiTextFromResponse(payload);
    if (!out)
      throw new Error(
        "Gemini Direct retornou resposta vazia para análise multimodal.",
      );
    return out;
  } catch (error) {
    throw new Error(
      `Falha na análise multimodal Gemini: ${String(error?.message || error)}`,
    );
  }
};

export const LegalAPIs = {
  /**
   * Busca dados de endereço via CEP (BrasilAPI) - Client Side OK (CORS Liberado)
   */
  fetchCEP: async (cep) => {
    try {
      const clean = cep.replace(/\D/g, "");
      if (clean.length !== 8) throw new Error("CEP inválido");

      const res = await fetch(`https://brasilapi.com.br/api/cep/v2/${clean}`);
      if (!res.ok) throw new Error("CEP não encontrado");

      return await res.json();
    } catch (error) {
      console.error("Erro CEP:", error);
      throw error;
    }
  },

  fetchCities: async (uf) => {
    try {
      const u = String(uf || "")
        .trim()
        .toUpperCase();
      if (!/^[A-Z]{2}$/.test(u)) throw new Error("UF inválida");

      const res = await fetch(
        `https://brasilapi.com.br/api/ibge/municipios/v1/${u}?providers=dados-abertos-br,gov,wikipedia`,
      );
      if (!res.ok) throw new Error("Falha ao carregar municípios");
      const data = await res.json();
      const rows = Array.isArray(data) ? data : [];
      return rows
        .map((r) => String(r?.nome || r?.name || "").trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "pt-BR"));
    } catch (error) {
      console.error("Erro Municipios:", error);
      throw error;
    }
  },

  geminiPredictField: async (fieldName, userInput) => {
    const { proxyUrl } = readGeminiRuntimeConfig();

    if (!proxyUrl) {
      throw new Error(
        "Gemini proxy não configurado (defina VITE_GEMINI_PROXY_URL).",
      );
    }

    const field = String(fieldName || "").trim() || "campo";
    const input = String(userInput || "").trim();
    if (!input) return "";

    const prompt = `Aja como um oficial de registro civil. Corrija e formalize este dado para um campo de ${field}: ${input}. Retorne apenas o termo corrigido, sem explicações.`;

    const response = await fetch(proxyUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt, fieldName: field, userInput: input }),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Erro Gemini Proxy (${response.status}): ${errText}`);
    }

    const data = await response.json();
    const out = String(data?.text || data?.output || data?.result || "").trim();
    return out;
  },

  generateTextWithGemini,

  /**
   * Busca dados de empresa via CNPJ (BrasilAPI) - Client Side OK
   */
  fetchCNPJ: async (cnpj) => {
    try {
      const clean = cnpj.replace(/\D/g, "");
      if (clean.length !== 14) throw new Error("CNPJ inválido");

      const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${clean}`);
      if (!res.ok) throw new Error("CNPJ não encontrado");

      return await res.json();
    } catch (error) {
      console.error("Erro CNPJ:", error);
      throw error;
    }
  },

  /**
   * Busca dados de advogado no Cadastro Nacional da OAB via backend proxy.
   * Utiliza o endpoint /oab/consultar do backend FastAPI para contornar CORS.
   */
  fetchOAB: async ({ inscricao, uf, nome } = {}) => {
    const safeInscricao = String(inscricao || "").trim();
    const safeUf = String(uf || "")
      .trim()
      .toUpperCase();
    const safeNome = String(nome || "").trim();

    if (!safeInscricao && !safeNome) {
      throw new Error("Informe o número da inscrição OAB ou nome do advogado.");
    }
    if (!safeUf) {
      throw new Error("UF é obrigatória.");
    }

    const BACKEND_URL =
      import.meta.env.VITE_BACKEND_URL || "http://127.0.0.1:8009";
    const url = `${BACKEND_URL.replace(/\/+$/, "")}/oab/consultar`;

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        inscricao: safeInscricao,
        uf: safeUf,
        nome: safeNome,
      }),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Erro OAB (${response.status}): ${errText}`);
    }

    return await response.json();
  },

  /**
   * Busca Processo no DataJud (CNJ) - VIA PROXY
   * A chamada direta foi removida para evitar bloqueio CORS.
   */
  fetchProcesso: async (cnj) => {
    const cleanCNJ = cnj.replace(/\D/g, "");

    // 1. Validação Básica
    if (cleanCNJ.length < 20) {
      console.warn("CNJ incompleto, usando Mock.");
      return parseDataJudResponse(getMockDataJud(cnj));
    }

    // 2. Tentativa Real via Proxy (Cloud Function)
    try {
      console.log(" Iniciando busca via Proxy RDA...");

      const response = await fetch(PROXY_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ numeroProcesso: cleanCNJ }),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Erro Proxy (${response.status}): ${errText}`);
      }

      const data = await response.json();

      if (data.hits && data.hits.hits.length > 0) {
        const realData = data.hits.hits[0]._source;
        console.log(" Dados reais recebidos do CNJ!");
        return parseDataJudResponse(realData);
      } else {
        console.warn(" Processo não encontrado na base do CNJ.");
        // Retorna null ou cai para o Mock dependendo da preferência
        // return parseDataJudResponse(getMockDataJud(cnj)); // Opcional: Fallback
      }
    } catch (error) {
      console.error(" Falha na conexão com Proxy:", error);
      console.warn(" Ativando Fallback (Dados Simulados) para não travar UX.");
    }

    // 3. Fallback (Mock/Simulação)
    // Se a Cloud Function não estiver deployada ou der erro, usamos o simulador.
    return parseDataJudResponse(getMockDataJud(cnj));
  },
};
